import { useEffect, useRef, useState } from 'react';
import Animated from "react-native-web/dist/exports/Animated";
import Easing from "react-native-web/dist/exports/Easing";
import Platform from "react-native-web/dist/exports/Platform";
import useSafeLayoutEffect from '../useSafeLayoutEffect';

// TODO: systematise animations
// https://github.com/telus/universal-design-system/issues/487
function useVerticalExpandAnimation(_ref) {
  let {
    containerHeight,
    isExpanded,
    tokens
  } = _ref;
  const [isAnimating, setIsAnimating] = useState(false);
  const [wasExpanded, setWasExpanded] = useState(isExpanded);
  const expandStateChanged = isExpanded !== wasExpanded;
  const expandAnimatedValue = useRef(new Animated.Value(0)).current;
  const elementRef = useRef(null);
  const {
    expandDuration,
    collapseDuration
  } = tokens;
  useSafeLayoutEffect(() => {
    if (expandStateChanged) {
      setIsAnimating(true);
      setWasExpanded(isExpanded);
    }
  }, [expandStateChanged, isExpanded]);
  useEffect(() => {
    const onComplete = () => setIsAnimating(false);
    if (Platform.OS === 'web') {
      if (!elementRef.current) return () => {};

      // React Native Web does not pass `onTransitionEnd` through, must attach manually.
      // https://github.com/necolas/react-native-web/pull/1713
      const element = elementRef.current;
      element.addEventListener('transitionend', onComplete);
      return () => element.removeEventListener('transitionend', onComplete);
    }
    const animationConfig = {
      duration: isExpanded ? expandDuration : collapseDuration,
      easing: Easing.inOut(Easing.ease),
      toValue: isExpanded ? containerHeight : 0,
      useNativeDriver: false
    };
    const animation = Animated.timing(expandAnimatedValue, animationConfig);
    animation.start(onComplete);
    return () => animation.stop();
  }, [isExpanded, expandAnimatedValue, containerHeight, expandDuration, collapseDuration]);
  const containerStyles = {};
  if (isAnimating || expandStateChanged) containerStyles.overflow = 'hidden';
  if (!isExpanded && !isAnimating && !expandStateChanged) {
    if (Platform.OS === 'web') {
      // Without `display: 'none', descendents are focusable on web even when collapsed.
      containerStyles.display = 'none';
    } else {
      // There's no `visibility: hidden` in React Native, and display: none causes a flicker on expand.
      // Without some form of hiding, some children leak through even when closed e.g. `List.Item` bullets.
      containerStyles.opacity = 0;
    }
  }

  // don't visually collapse the container until we have it measured
  if (containerHeight === null) {
    if (Platform.OS === 'web') {
      // on web we can hide the contents until we have the container measured and avoid occasional jitter
      // this won't work on native platforms
      containerStyles.height = 0;
      containerStyles.display = 'none';
    }
  } else if (Platform.OS === 'web') {
    const transitionDuration = isExpanded ? expandDuration : collapseDuration;
    containerStyles.transition = `height ${transitionDuration}ms ease-in-out`;
    containerStyles.height = isExpanded ? containerHeight : 0;
  } else if (Platform.OS === 'ios' && isExpanded && !isAnimating && !expandStateChanged && typeof containerHeight === 'number') {
    // iOS reflows text while the height animation is in progress. Sometimes, it hits a timing bug where
    // it fails to reflow properly after the last animation frame, leaving the last line of text not visible.
    // Force reflow after animation completes with a static height non-identical to the last animated height.
    containerStyles.height = containerHeight + 1;
  } else {
    containerStyles.height = expandAnimatedValue;
  }
  return [containerStyles, elementRef];
}
export default useVerticalExpandAnimation;